import { useMemo } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { useGetList, useGetMany, type Identifier } from "ra-core";
import type {
  CalendarEventRecord,
  Contact,
  Deal,
  OrganizationMember,
  Task,
} from "@/components/atomic-crm/types";
import { formatOrganizationMemberName } from "@/modules/billing/billingUtils";
import {
  buildCalendarEntryEvent,
  buildDealCalendarEvents,
  buildTaskCalendarEvent,
  getVisibleRange,
  groupEventsByDate,
  type CalendarEvent,
  type CalendarView,
} from "@/modules/calendar/calendarUtils";

const getContactName = (contact?: Contact | null) =>
  contact
    ? [contact.first_name, contact.last_name].filter(Boolean).join(" ") ||
      "Contact"
    : null;

export const useCalendarEvents = ({
  anchor,
  view,
  includeDoneTasks = false,
  includeCompletedReminders = false,
  projectId = null,
  expandRangeMonths = 0,
}: {
  anchor: Date;
  view: CalendarView;
  includeDoneTasks?: boolean;
  includeCompletedReminders?: boolean;
  projectId?: Identifier | null;
  /** Widen the fetched date range (e.g. while searching). */
  expandRangeMonths?: number;
}) => {
  const range = useMemo(
    () =>
      getVisibleRange(anchor, view, {
        expandMonths: expandRangeMonths > 0 ? expandRangeMonths : undefined,
      }),
    [anchor, expandRangeMonths, view],
  );
  const projectFilter =
    projectId != null && projectId !== "" ? { "deal_id@eq": projectId } : {};

  const { data: tasks = [], isPending: isTasksPending } = useGetList<Task>(
    "tasks",
    {
      filter: {
        "due_date@gte": range.start,
        "due_date@lte": range.end,
        ...(includeDoneTasks ? {} : { "done_date@is": null }),
        ...projectFilter,
      },
      pagination: { page: 1, perPage: 500 },
      sort: { field: "due_date", order: "ASC" },
    },
    // Keep the previous range while the next loads (no flash when paging).
    { staleTime: 30_000, placeholderData: keepPreviousData },
  );

  const { data: reminders = [], isPending: isRemindersPending } =
    useGetList<CalendarEventRecord>(
      "calendar_events",
      {
        filter: {
          "event_date@gte": range.start,
          "event_date@lte": range.end,
          ...(includeCompletedReminders ? {} : { "completed_at@is": null }),
          ...projectFilter,
        },
        pagination: { page: 1, perPage: 500 },
        sort: { field: "event_date", order: "ASC" },
      },
      { staleTime: 30_000, placeholderData: keepPreviousData },
    );

  const memberIds = useMemo(
    () =>
      Array.from(
        new Set(
          reminders
            .map((entry) => entry.organization_member_id)
            .filter((id) => id != null)
            .map(String),
        ),
      ),
    [reminders],
  );

  const contactIds = useMemo(
    () =>
      Array.from(
        new Set(
          reminders
            .map((entry) => entry.contact_id)
            .filter((id) => id != null)
            .map(String),
        ),
      ),
    [reminders],
  );

  const { data: members = [] } = useGetMany<OrganizationMember>(
    "organization_members",
    { ids: memberIds },
    { enabled: memberIds.length > 0, staleTime: 60_000 },
  );

  const { data: contacts = [] } = useGetMany<Contact>(
    "contacts",
    { ids: contactIds },
    { enabled: contactIds.length > 0, staleTime: 60_000 },
  );

  const membersById = useMemo(() => {
    const map = new Map<string, OrganizationMember>();
    members.forEach((member) => map.set(String(member.id), member));
    return map;
  }, [members]);

  const contactsById = useMemo(() => {
    const map = new Map<string, Contact>();
    contacts.forEach((contact) => map.set(String(contact.id), contact));
    return map;
  }, [contacts]);

  const { data: deals = [], isPending: isDealsPending } = useGetList<Deal>(
    "deals",
    {
      filter:
        projectId != null && projectId !== "" ? { "id@eq": projectId } : {},
      pagination: { page: 1, perPage: 500 },
      sort: { field: "name", order: "ASC" },
    },
    { staleTime: 60_000 },
  );

  const events = useMemo(() => {
    const taskEvents = tasks
      .map(buildTaskCalendarEvent)
      .filter((event): event is CalendarEvent => event != null);

    const reminderEvents = reminders
      .map((entry) =>
        buildCalendarEntryEvent(entry, {
          assignedName: entry.organization_member_id
            ? formatOrganizationMemberName(
                membersById.get(String(entry.organization_member_id)),
              )
            : null,
          contactName: entry.contact_id
            ? getContactName(contactsById.get(String(entry.contact_id)))
            : null,
        }),
      )
      .filter((event): event is CalendarEvent => event != null);

    const dealEvents = deals
      .flatMap(buildDealCalendarEvents)
      .filter((event) => event.date >= range.start && event.date <= range.end);

    return [...taskEvents, ...reminderEvents, ...dealEvents];
  }, [
    contactsById,
    deals,
    membersById,
    range.end,
    range.start,
    reminders,
    tasks,
  ]);

  const eventsByDate = useMemo(() => groupEventsByDate(events), [events]);

  return {
    events,
    eventsByDate,
    deals,
    range,
    isPending: isTasksPending || isRemindersPending || isDealsPending,
  };
};
